// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

// TODO: if browser height is very small and there are many menu items, menu items in desktop mode may in theory be hidden below the bottom of the browser

import React, { useEffect, useState, useContext, useMemo, useCallback } from 'react';
import classnames from 'classnames';
import Cart, { CartProps } from './Cart/Cart';
import TopBar from './TopBar/TopBar';
import SiteLinks from './SiteLinks/SiteLinks';
import { TopBarItemShape } from './SiteLink/SiteLink';
import Search, { SearchProps } from './Search/Search';
import Menu from './Menu/Menu';
import { MenuItemShape } from './Menu/MenuItem';
import styles from './Navigation.module.scss';
import usePreventScroll from '../usePreventScroll/usePreventScroll';
import { addInternalMenuItemsProperties, flattenMenuItems, getMenuItemsParentIds } from './navigationUtils';
import {
  Backdrop,
  Breakpoint,
  BreakpointProvider,
  Div,
  Element,
  Drawer,
  Flex,
  Icon,
  LinkAdapter,
  Logo,
  MarginSpace,
} from '@gaiads/telia-react-component-library';
import UserMenu, { UserMenuLink } from '../Login/UserMenu';
import Login, { AdditionalLoginLinkShape, LoginProps } from '../Login/Login';

export const MainSiteNavigationComponentId = 'main-site-navigation-component';

type MenuItemWithoutLevelAndId = Omit<MenuItemShape, 'level' | 'id' | 'menuItems'> & {
  menuItems?: MenuItemWithoutLevelAndId[];
};

export type NavigationProps = {
  /** If applied, adds a cart icon with a number   */
  cart?: CartProps;
  /** Sets anchor tag to the logo with the href and aria-label */
  logo: {
    /** Where does clicking on the logo take you to */
    link: string;
    /** Aria label for the logo link */
    linkAriaLabel: string;
  };
  /** Market segment links */
  segments?: TopBarItemShape[];
  /** Language links */
  languages?: TopBarItemShape[];
  /** Search parameters */
  search?: Omit<SearchProps, 'isOpen' | 'onSearchToggle'>;
  /** Login form parameters  */
  login: LoginProps & { onLogout: () => void };
  /** Is user logged in */
  userMenu:
    | false
    | {
        /** Username showed in profile drawer when logged-in */
        username: string;
        /** Link showed when user is logged in */
        userMenuLinks: UserMenuLink[];
        /** Logout button label showed in drawer when logged-in */
        logoutButtonLabel: string;
        /** User initials showed in avatar when logged-in */
        userInitials: string;
        /** Additional links on the bottom of the logged in menu */
        additionalLinks?: AdditionalLoginLinkShape[];
      };
  /** Menu items */
  menuItems?: Array<MenuItemWithoutLevelAndId & { active?: boolean }>;
  /** Custom link or for injecting third party routing link */
  linkAdapter?: LinkAdapter;
};

const desktopBreakpoints = ['md', 'lg', 'xlg', 'xxlg'];

const Navigation: React.FunctionComponent<NavigationProps> = ({
  cart,
  logo,
  segments,
  languages,
  search,
  login,
  userMenu,
  linkAdapter,
  menuItems,
}: NavigationProps) => {
  const [openMenuItems, setOpenMenuItems] = useState<string[]>([]);

  // derive menuItems from openMenuItems and menuItems to create menuItemShapeList
  const menuItemShapeList = useMemo(() => {
    return addInternalMenuItemsProperties(menuItems, openMenuItems);
  }, [menuItems, openMenuItems]);

  const flatMenuItems = useMemo(() => {
    return flattenMenuItems(menuItemShapeList);
  }, [menuItemShapeList]);

  const [loginMenuOpen, setLoginMenuOpen] = useState(false);
  const onMenuItemClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>, menuItemId: string, level: number, locked: boolean) => {
      event.stopPropagation();
      if (locked) {
        setLoginMenuOpen(true);
        return;
      }
      const menuItem = flatMenuItems.find(it => it.id === menuItemId) as MenuItemShape;
      if (!menuItem.menuItems?.length) {
        // we assume that this menuItem is a link, so we close all menus
        setOpenMenuItems([]);
        return;
      }
      const parentIds = getMenuItemsParentIds(menuItemId, flatMenuItems);
      // close all menus except parents, granparents ... etc. of menuitem
      const nextOpenMenuItems = openMenuItems.filter(it => parentIds.includes(it));
      if (!menuItem.isOpen) {
        nextOpenMenuItems.push(menuItemId);
      }
      setOpenMenuItems(nextOpenMenuItems);
    },
    [flatMenuItems, openMenuItems],
  );

  const anyMenuItemsOpen = useMemo(() => {
    return flatMenuItems.some(it => it.isOpen);
  }, [flatMenuItems]);

  const breakpoint = useContext(BreakpointProvider.Context);

  // TODO combine mobileMenuOpen, searchOpen and openMenuItems into a single state?
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    // TODO: How to test stuff that initiates in desktop sizes but only has side effects in mobile sizes?
    //       The Breakpoint component doesn't render its contents at all when when outside the breakpoint boundaries.
    /* istanbul ignore else */
    if (anyMenuItemsOpen) {
      setLoginMenuOpen(false);
      setMobileMenuOpen(true);
    } else {
      if (desktopBreakpoints.includes(breakpoint)) {
        setMobileMenuOpen(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anyMenuItemsOpen]);

  useEffect(() => {
    if (mobileMenuOpen) {
      setLoginMenuOpen(false);
      setSearchOpen(false);
    } else {
      setOpenMenuItems([]);
    }
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (searchOpen) {
      setLoginMenuOpen(false);
      setMobileMenuOpen(false);
      setOpenMenuItems([]);
    }
  }, [searchOpen]);

  useEffect(() => {
    if (loginMenuOpen) {
      setSearchOpen(false);
      setMobileMenuOpen(false);
      setOpenMenuItems([]);
    }
  }, [loginMenuOpen]);

  /* istanbul ignore next */
  useEffect(() => {
    const shouldCloseMobileMenu = desktopBreakpoints.includes(breakpoint) && mobileMenuOpen && !anyMenuItemsOpen;

    if (shouldCloseMobileMenu) {
      setMobileMenuOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breakpoint]);

  const shouldPreventScroll = loginMenuOpen || mobileMenuOpen || anyMenuItemsOpen || searchOpen;

  usePreventScroll(shouldPreventScroll);

  const userIconOrInitials = userMenu ? (
    <>
      <span data-testid="open-login" className={styles.navigation_userInitials}>
        {userMenu.userInitials.toUpperCase()}
      </span>
    </>
  ) : (
    <Icon data-testid="open-login" name="end-user" size="lg" color="black" />
  );

  const closeIcon = <Icon data-testid="close-login" name="close" size="lg" color="black" />;

  return (
    <>
      <Backdrop
        data-testid="backdrop"
        isVisible={loginMenuOpen || mobileMenuOpen || anyMenuItemsOpen}
        onCloseClick={() => {
          setLoginMenuOpen(false);
          setMobileMenuOpen(false);
          setOpenMenuItems([]);
        }}
      />

      <Div
        id={MainSiteNavigationComponentId}
        className={[styles.navigation, { [styles.navigation__mobileMenuOpen]: mobileMenuOpen }]}
        backgroundColor="white"
      >
        <MarginSpace>
          <Breakpoint widerThan="md">
            <TopBar segments={segments} languages={languages} linkAdapter={linkAdapter} />
          </Breakpoint>

          <Flex className={styles.navigation_mainContainer} spaceBetween centeredVertically padding={{ vertical: 'sm' }}>
            <Flex>
              <a href={logo.link} className={styles.navigation_logo} aria-label={logo.linkAriaLabel}>
                <Logo color="purple500" size="xlg" />
              </a>

              {Boolean(menuItemShapeList.length) && (
                <Breakpoint widerThan="md">
                  <Div
                    data-testid="desktop-menu-items"
                    className={[
                      styles.navigation_menuContainer,
                      {
                        [styles.navigation_menuContainer__slideHidden]: searchOpen,
                      },
                    ]}
                  >
                    <Menu
                      data-testid="desktop-menu-component"
                      menuItems={menuItemShapeList}
                      onMenuItemClick={onMenuItemClick}
                      linkAdapter={linkAdapter}
                    />
                  </Div>
                </Breakpoint>
              )}
            </Flex>

            <Flex centeredVertically>
              {search && (
                <Div className={styles.navigation_iconMenuItem}>
                  <Search data-testid="search-component" isOpen={searchOpen} onSearchToggle={() => setSearchOpen(!searchOpen)} {...search} />
                </Div>
              )}

              {cart && (
                <Div className={styles.navigation_iconMenuItem}>
                  <Cart {...cart} />
                </Div>
              )}

              <Element
                tagName="button"
                data-testid="login-icon"
                onClick={() => setLoginMenuOpen(!loginMenuOpen)}
                className={styles.navigation_iconMenuItem}
              >
                {loginMenuOpen ? closeIcon : userIconOrInitials}
              </Element>

              <Breakpoint narrowerThan="md">
                <Element
                  tagName="button"
                  data-testid="hamburger"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className={classnames(styles.navigation_iconMenuItem, styles.navigation_hamburger, {
                    [styles.navigation_hamburger__active]: mobileMenuOpen,
                  })}
                >
                  <span></span>
                  <span></span>
                  <span></span>
                  <span></span>
                </Element>
              </Breakpoint>
            </Flex>
          </Flex>
        </MarginSpace>
      </Div>

      <Breakpoint narrowerThan="md">
        <Div className={styles.navigation_mobileMenuContainer}>
          <Drawer
            data-testid="mobile-menu"
            isOpen={mobileMenuOpen}
            showBackdrop={false}
            onCloseClick={() => setMobileMenuOpen(false)}
            automaticScrollPrevention={false}
          >
            {segments && (
              <Div backgroundColor="white" className={styles.navigation_segmentLinksContainer}>
                <MarginSpace>
                  <SiteLinks
                    className={styles.navigation_segmentLinks}
                    data-testid="segment-site-links"
                    links={segments}
                    alignBottom
                    linkAdapter={linkAdapter}
                  />
                </MarginSpace>
              </Div>
            )}

            {Boolean(menuItemShapeList.length) && (
              <Div data-testid="mobile-menu-items" className={styles.navigation_menuContainer}>
                <Menu
                  data-testid="mobile-menu-component"
                  menuItems={menuItemShapeList}
                  onMenuItemClick={onMenuItemClick}
                  forMobile={true}
                  linkAdapter={linkAdapter}
                />
              </Div>
            )}

            {languages && (
              <Div backgroundColor="gray200">
                <MarginSpace>
                  <SiteLinks
                    className={styles.navigation_languageLinks}
                    data-testid="language-site-links"
                    links={languages}
                    desktopStylesInMobile={true}
                    centeredVertically
                    linkAdapter={linkAdapter}
                  />
                </MarginSpace>
              </Div>
            )}
          </Drawer>
        </Div>
      </Breakpoint>

      {userMenu ? (
        <MarginSpace className={styles.navigation_loggedInContainer}>
          <Div className={styles.navigation_loggedInDrawerContainer}>
            <Drawer
              backgroundColor="white"
              data-testid="logged-in-menu"
              isOpen={loginMenuOpen}
              showBackdrop={false}
              onCloseClick={() => setLoginMenuOpen(false)}
              automaticScrollPrevention={false}
            >
              <UserMenu
                username={userMenu.username}
                userMenuLinks={userMenu.userMenuLinks}
                logoutButton={{
                  logoutButtonLabel: userMenu.logoutButtonLabel,
                  onLogoutButtonClick: () => {
                    login.onLogout();
                    setLoginMenuOpen(false);
                  },
                }}
                additionalLinks={userMenu.additionalLinks}
              />
            </Drawer>
          </Div>
        </MarginSpace>
      ) : (
        <Div className={styles.navigation_loggedOutContainer}>
          <Drawer
            data-testid="login-menu"
            isOpen={loginMenuOpen}
            showBackdrop={false}
            onCloseClick={() => setLoginMenuOpen(false)}
            automaticScrollPrevention={false}
          >
            <Login {...login} />
          </Drawer>
        </Div>
      )}
    </>
  );
};

export default Navigation;
